import { ComponentConfig, usePuck } from '@measured/puck';
import { useEffect, useState } from 'react';
import { Switch } from '@mui/material';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import { marginFields, MarginProps } from '../../../puck/reusable-props/margin';
import {
  paddingFields,
  PaddingProps,
} from '../../../puck/reusable-props/padding';
import showComponent, {
  showComponentProps,
} from '../../../puck/reusable-props/showComponent';
import { ReduxSelectorProps } from '../../../services/constants';
import CustomTooltip from '../../../../components/common/tooltip';
import OrderSummaryHeader from '../../../puck/reusable-props/orderSummaryHeader';
import { getIsEdit, getIsView } from '../../../utils/constants';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  viewPorts,
} from '../../../utils/helper';
import AccordionButton from '../customComponents/dropDown';
import CartDetails from '../CartDetails';
import PostUpsellOrderSummaryContent from '../../../puck/reusable-props/postUpsellorderSummaryContent';
import { backgroungFields } from '../../../puck/reusable-props/backgroungFields';
import { OrderAndUpSellSummary } from '../../../interface/commonInterface';
import { ICalculatedFee } from '../../../interface/checkoutInterface';

export type Props = {
  fontSize: string;
  fontWeight: string;
  showOnMobile: boolean;
  showOnTablet: boolean;
  showOnDesktop: boolean;
  textColor: string;
};

export type PostUpsellOrderSummaryContentPropsDefault = {
  GrandTotal: Props;
  SalesTax: Props;
  CalculatedFees: Props;
  showOnDesktop: boolean;
  showOnTablet: boolean;
  showOnMobile: boolean;
  border: IBorderValueProps;
  backgroundColor: string;
  productDetails: IOrdersProductDetails;
} & MarginProps &
  PaddingProps;

export type IHeaderProps = {
  openText: string;
  closeText: string;
};

export type OrderHeaderProps = {
  HeaderText: {
    textColor: string;
    fontSize: string;
    fontWeight: string;
    openText: string;
    closeText: string;
  };
  showOnDesktop: boolean;
  showOnTablet: boolean;
  showOnMobile: boolean;
  backgroundColor: string;
} & MarginProps &
  PaddingProps;

export type backgroundColorProps = {
  backgroundColor: string;
};

export type IOrdersProductDetails = {
  productName: Props;
  productPrice: Props;
  productVarient: Props;
  productQuantity: Props;
  showOnDesktop: boolean;
  showOnTablet: boolean;
  showOnMobile: boolean;
} & MarginProps &
  PaddingProps;

export type PostUpsellOrderSummaryProps = {
  content: PostUpsellOrderSummaryContentPropsDefault;
  header: OrderHeaderProps;
  isDefaultOpen: boolean;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
} & showComponentProps &
  MarginProps &
  PaddingProps;

export const PostUpsellOrderSummary = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<PostUpsellOrderSummaryProps> => {
  return {
    label: (
      <CustomTooltip title="PostUpsellOrderSummary">
        <span>Post Upsell Order Summary</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      ...backgroungFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      isDefaultOpen: {
        type: 'custom',
        label: 'Default Open',
        render: ({ value, onChange }) => (
          <div className="flex gap-2 items-center justify-between">
            <label>Default Collapsed</label>
            <Switch
              checked={value}
              onChange={e => onChange(e.target.checked)}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </div>
        ),
      },

      header: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <>
            <OrderSummaryHeader
              value={value}
              onChange={onChange}
              title="Header"
              showHover={true}
            />
          </>
        ),
      },
      content: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <>
            <PostUpsellOrderSummaryContent
              value={value}
              onChange={onChange}
              title="Content"
              showHover={true}
            />
          </>
        ),
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      isDefaultOpen: false,
      ...borderProps,
      background: {
        color: '#00000',
        isInherit: false,
      },
      margin: {
        desktop: {
          top: '6',
          bottom: '6',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '6',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '4',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      content: {
        margin: {
          desktop: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
          tablet: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
          mobile: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
        },
        padding: {
          deviceType: 'desktop',
          paddingBottom: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingLeft: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingRight: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingTop: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
        },
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
        GrandTotal: {
          textColor: '#000000',
          fontSize: 'text-base',
          fontWeight: 'font-semibold',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
        },
        SalesTax: {
          textColor: '#000000',
          fontSize: 'text-base',
          fontWeight: 'font-semibold',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
        },
        CalculatedFees: {
          textColor: '#000000',
          fontSize: 'text-base',
          fontWeight: 'font-semibold',
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
        },
        ...borderProps,
        backgroundColor: '#ffffff',
        productDetails: {
          margin: {
            desktop: {
              top: '0',
              bottom: '0',
              right: '0',
              left: '0',
            },
            tablet: {
              top: '0',
              bottom: '0',
              right: '0',
              left: '0',
            },
            mobile: {
              top: '0',
              bottom: '0',
              right: '0',
              left: '0',
            },
          },
          padding: {
            deviceType: 'desktop',
            paddingBottom: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
            paddingLeft: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
            paddingRight: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
            paddingTop: {
              desktop: '0',
              mobile: '0',
              tablet: '0',
            },
          },
          showOnDesktop: true,
          showOnTablet: true,
          showOnMobile: true,
          productName: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
          productPrice: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
          productVarient: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
          productQuantity: {
            textColor: '#4B5563',
            fontSize: 'text-sm',
            fontWeight: 'font-normal',
            showOnDesktop: true,
            showOnTablet: true,
            showOnMobile: true,
          },
        },
      },
      header: {
        showOnDesktop: true,
        showOnTablet: true,
        showOnMobile: true,
        margin: {
          desktop: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
          tablet: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
          mobile: {
            top: '0',
            bottom: '0',
            right: '0',
            left: '0',
          },
        },
        padding: {
          deviceType: 'desktop',
          paddingBottom: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingLeft: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingRight: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
          paddingTop: {
            desktop: '0',
            mobile: '0',
            tablet: '0',
          },
        },
        backgroundColor: '#000000',
        HeaderText: {
          textColor: '#ffffff',
          fontSize: 'text-sm',
          fontWeight: 'font-normal',
          openText: 'Show Post Upsell Order Summary',
          closeText: 'Hide Post Upsell Order Summary',
        },
      },
    },
    render: ({
      content,
      header,
      showComponent,
      margin,
      padding,
      background,
      border,
      isDefaultOpen,
      editMode,
    }) => {
      const currentWidth = useAppSelector(state => state.store.currentWidth);
      let width = currentWidth;
      const isEditPage = getIsEdit();
      if (isEditPage) {
        const { appState } = usePuck();
        width = appState?.ui?.viewports?.current?.width;
      }
      const currentDeviceType = getcurrentDeviceType(currentWidth, editMode);
      const [isDropdownOpen, setIsDropdownOpen] = useState(!isDefaultOpen);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const headerMarginStyles = getMarginStyles(
        header.margin,
        currentDeviceType,
      );
      const contentMarginStyles = getMarginStyles(
        content.margin,
        currentDeviceType,
      );

      function addCalculatedFees(
        obj1: ICalculatedFee[],
        obj2: ICalculatedFee[],
      ): ICalculatedFee[] {
        const result: ICalculatedFee[] = [];

        obj1.forEach(fee1 => {
          const fee2 = obj2.find(f => f.FeeName === fee1.FeeName);

          if (fee2) {
            const amount1 =
              typeof fee1.ComputedAmount === 'string'
                ? parseFloat(fee1.ComputedAmount)
                : fee1.ComputedAmount;

            const amount2 =
              typeof fee2.ComputedAmount === 'string'
                ? parseFloat(fee2.ComputedAmount)
                : fee2.ComputedAmount;

            const totalAmount = amount1 + amount2;

            result.push({
              FeeName: fee1.FeeName,
              ComputedAmount: totalAmount.toFixed(2),
            });
          } else {
            result.push({ ...fee1 });
          }
        });

        obj2.forEach(fee2 => {
          const existingFee = result.find(f => f.FeeName === fee2.FeeName);
          if (!existingFee) {
            result.push({ ...fee2 });
          }
        });

        return result;
      }
      const orderDetails = useAppSelector(
        state =>
          state.orderDetails?.slice(1)?.reduce(
            (acc: OrderAndUpSellSummary, curr: OrderAndUpSellSummary) => {
              return {
                orderTotal: (
                  Number(acc.orderTotal) + Number(curr.orderTotal)
                ).toString(),
                subTotal: (
                  Number(acc.subTotal) + Number(curr.subTotal)
                ).toString(),
                shippingCrg: (
                  Number(acc.shippingCrg) + Number(curr.shippingCrg)
                ).toString(),
                tax: (Number(acc.tax) + Number(curr.tax)).toString(),
                currencyCode: curr.currencyCode,
                currency_rate: curr.currency_rate,
                currencySymbol: curr.currencySymbol,
                discount: '',
                products: [
                  ...acc.products,
                  ...curr.products.map(product => ({
                    productDetails: product.productDetails,
                    quantity: product.quantity,
                    subTitle: product.subTitle,
                    productName: product.productName,
                    productPrice: product.productPrice,
                    currencySymbol: curr.currencySymbol,
                    variantId: product.variantId,
                  })),
                ],
                SalesTaxAmount: (
                  Number(curr.SalesTaxAmount) + Number(acc.SalesTaxAmount)
                )?.toFixed(2),
                CalculatedFees: addCalculatedFees(
                  acc.CalculatedFees || [],
                  curr.CalculatedFees || [],
                ),
              };
            },
            {
              orderTotal: '',
              subTotal: '',
              shippingCrg: '',
              tax: '',
              currencyCode: '',
              currency_rate: '',
              currencySymbol: '',
              discount: '',
              products: [],
              SalesTaxAmount: '',
              CalculatedFees: [],
            },
          ) || null,
      );

      useEffect(() => {
        setIsDropdownOpen(!isDefaultOpen);
      }, [isDefaultOpen]);

      return (
        <>
          {showComponent?.includes(currentDeviceType) &&
            (orderDetails || getIsEdit()) && (
              <div
                className={`
             ${border?.borderStyle?.[currentDeviceType]}`}
                style={{
                  backgroundColor: background?.isInherit
                    ? 'inherit'
                    : background?.color,
                  ...getBorderProps(border, currentDeviceType),
                  ...marginStyles,
                  ...getPaddingStyles(padding, currentDeviceType),
                }}>
                {((header?.showOnMobile &&
                  width &&
                  width <= viewPorts.mobile) ||
                  (header?.showOnTablet &&
                    width &&
                    width > viewPorts.mobile &&
                    width <= viewPorts.tablet) ||
                  (header?.showOnDesktop &&
                    width &&
                    width > viewPorts.tablet)) && (
                  <div
                    style={{
                      color: header?.HeaderText?.textColor,
                      backgroundColor: header?.backgroundColor,
                      ...headerMarginStyles,
                      ...getPaddingStyles(header?.padding, currentDeviceType),
                    }}
                    className={`
                      ${header?.HeaderText?.fontSize} ${header?.HeaderText?.fontWeight}
                      `}>
                    <AccordionButton
                      isOpen={isDropdownOpen}
                      onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
                      showLabel={header?.HeaderText?.openText}
                      hideLabel={header?.HeaderText?.closeText}
                      color={header?.HeaderText?.textColor}
                      fontSize={header?.HeaderText?.fontSize}
                      fontWeight={header?.HeaderText?.fontWeight}
                    />
                  </div>
                )}

                {isDropdownOpen && (
                  <div
                    className={`
                  ${content?.border?.borderStyle?.[currentDeviceType]}`}
                    style={{
                      background: content?.backgroundColor,
                      ...getBorderProps(content.border, currentDeviceType),
                      ...contentMarginStyles,
                      ...getPaddingStyles(content?.padding, currentDeviceType),
                    }}>
                    {(content?.productDetails?.showOnMobile &&
                      width &&
                      width <= viewPorts.mobile) ||
                    (content?.productDetails?.showOnTablet &&
                      width &&
                      width > viewPorts.mobile &&
                      width <= viewPorts.tablet) ||
                    (content?.productDetails?.showOnDesktop &&
                      width &&
                      width > viewPorts?.tablet) ? (
                      <CartDetails
                        value={orderDetails}
                        productDetails={content?.productDetails}
                        width={width}
                        currentDeviceType={currentDeviceType}
                      />
                    ) : null}

                    {(content?.showOnMobile &&
                      width &&
                      width <= viewPorts.mobile) ||
                    (content?.showOnTablet &&
                      width &&
                      width > viewPorts.mobile &&
                      width <= viewPorts.tablet) ||
                    (content?.showOnDesktop &&
                      width &&
                      width > viewPorts?.tablet) ? (
                      <>
                        {isEditPage || getIsView() ? (
                          <div
                            className={`flex items-center pb-4 justify-between ${content?.SalesTax?.fontSize} ${content?.SalesTax?.fontWeight}`}
                            style={{
                              color: content?.SalesTax?.textColor,
                            }}>
                            <dt>Sales Tax</dt>
                            <dd>$0.00</dd>
                          </div>
                        ) : (
                          <div
                            className={`space-y-6  text-sm font-medium  lg:block`}>
                            {((content?.SalesTax?.showOnMobile &&
                              width &&
                              width <= viewPorts.mobile) ||
                              (content?.SalesTax?.showOnTablet &&
                                width &&
                                width > viewPorts.mobile &&
                                width <= viewPorts.tablet) ||
                              (content?.SalesTax?.showOnDesktop &&
                                width &&
                                width > viewPorts.tablet)) &&
                              Boolean(Number(orderDetails?.SalesTaxAmount)) && (
                                <div
                                  className={`flex items-center justify-between  pt-6 ${content?.SalesTax.fontSize} ${content?.SalesTax.fontWeight}`}
                                  style={{
                                    color: content?.SalesTax?.textColor,
                                  }}>
                                  <dt className="">Sales Tax</dt>
                                  <dd className="">
                                    {orderDetails?.currencySymbol ?? '$'}
                                    {orderDetails?.SalesTaxAmount || 0.0}
                                  </dd>
                                </div>
                              )}
                          </div>
                        )}
                        {isEditPage || getIsView() ? (
                          <div
                            className={`flex items-center justify-between ${content?.CalculatedFees?.fontSize} ${content?.CalculatedFees?.fontWeight}`}
                            style={{
                              color: content?.CalculatedFees?.textColor,
                            }}>
                            <dt>Calculated Fee</dt>
                            <dd>$0.00</dd>
                          </div>
                        ) : (
                          <div
                            className={`space-y-4 pt-4 text-sm font-medium  lg:block`}>
                            {((content?.CalculatedFees?.showOnMobile &&
                              width &&
                              width <= viewPorts.mobile) ||
                              (content?.CalculatedFees?.showOnTablet &&
                                width &&
                                width > viewPorts.mobile &&
                                width <= viewPorts.tablet) ||
                              (content?.CalculatedFees?.showOnDesktop &&
                                width &&
                                width > viewPorts.tablet)) &&
                              orderDetails?.CalculatedFees &&
                              orderDetails?.CalculatedFees.map(
                                (fee: ICalculatedFee, index: number) => (
                                  <div
                                    key={index}
                                    className={`flex items-center justify-between  ${content.CalculatedFees?.fontSize} ${content.CalculatedFees?.fontWeight}`}
                                    style={{
                                      color: content.CalculatedFees?.textColor,
                                    }}>
                                    <dt>{fee.FeeName || 'Calculated Fee'}</dt>
                                    <dd>
                                      {orderDetails?.currencySymbol ?? '$'}
                                      {Number(
                                        fee?.ComputedAmount || '0',
                                      )?.toFixed(2) || 0.0}
                                    </dd>
                                  </div>
                                ),
                              )}
                          </div>
                        )}

                        <div
                          className={`space-y-6  text-sm font-medium  lg:block`}>
                          {((content?.GrandTotal?.showOnMobile &&
                            width &&
                            width <= viewPorts.mobile) ||
                            (content?.GrandTotal?.showOnTablet &&
                              width &&
                              width > viewPorts.mobile &&
                              width <= viewPorts.tablet) ||
                            (content?.GrandTotal?.showOnDesktop &&
                              width &&
                              width > viewPorts.tablet)) && (
                            <div
                              className={`flex items-center justify-between  pt-6 ${content?.GrandTotal.fontSize} ${content?.GrandTotal.fontWeight}`}
                              style={{ color: content?.GrandTotal?.textColor }}>
                              <dt className="">Grand Total</dt>
                              <dd className="">
                                {orderDetails?.currencySymbol ?? '$'}
                                {Number(orderDetails?.orderTotal).toFixed(2) ||
                                  0.0}
                              </dd>
                            </div>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            )}
        </>
      );
    },
  };
};
